import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Briefcase,
  CarFront,
  Download,
  Forward,
  Mail,
  Phone,
  StickyNote,
  Trash2,
  Truck,
} from "lucide-react";
import { Seo } from "@/components/Seo";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { CardSkeleton, FeedbackMessage } from "@/components/ui/feedback";
import { Label, Textarea } from "@/components/ui/input";
import { DemoBanner } from "@/components/portal/DemoBanner";
import { APPLY_TYPE_LABEL, type JobApplyType } from "@/content/jobs";
import { DEPARTMENT_LABEL, canManageRecruiting } from "@/features/auth/types";
import { useAuth } from "@/features/auth/useAuth";
import {
  addApplicationNote,
  deleteApplication,
  deleteApplicationNote,
  forwardApplication,
  getResumeUrl,
  listApplicationForwardUsers,
  listApplications,
  updateApplication,
  type ApplicationLead,
  type ApplicationNote,
  type ApplicationStatus,
  type ForwardUser,
} from "@/features/applications/repo";

const statusVariant: Record<ApplicationStatus, "default" | "warning" | "success" | "danger"> = {
  new: "warning",
  in_review: "default",
  approved: "success",
  rejected: "danger",
};

const statusLabels: Record<ApplicationStatus, string> = {
  new: "New",
  in_review: "In review",
  approved: "Approved",
  rejected: "Rejected",
};

const typeIcon: Record<JobApplyType, typeof Truck> = {
  driver: Truck,
  carrier: CarFront,
  office: Briefcase,
};

const filters: Array<{ label: string; value: "all" | JobApplyType }> = [
  { label: "All", value: "all" },
  { label: "Drivers", value: "driver" },
  { label: "Owner-operators", value: "carrier" },
  { label: "Office", value: "office" },
];

const statusFilters: Array<{ label: string; value: "all" | ApplicationStatus }> = [
  { label: "All statuses", value: "all" },
  { label: "New", value: "new" },
  { label: "In review", value: "in_review" },
  { label: "Approved", value: "approved" },
  { label: "Rejected", value: "rejected" },
];

const sourceLabel: Record<string, string> = {
  google: "Google / web search",
  indeed: "Indeed or job board",
  facebook: "Facebook / social media",
  referral: "Referral",
  truck: "Saw a Bull & Bear truck",
  other: "Other",
};

const fmt = (iso: string) =>
  new Date(iso).toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });

export default function InternalApplications() {
  const { user } = useAuth();
  const canForwardApplications = canManageRecruiting(user);
  const scopeNote =
    user?.staffLevel === "manager" && user.department === "hr"
      ? "Showing HR / Recruiting applications, plus all driver & owner-operator applicants."
      : user?.staffLevel === "manager" && user.department
      ? `Showing ${DEPARTMENT_LABEL[user.department]} applications only.`
      : user?.staffLevel === "admin"
        ? "You see applications across all departments."
      : "Showing applications forwarded to you.";
  const [apps, setApps] = useState<ApplicationLead[]>([]);
  const [forwardUsers, setForwardUsers] = useState<ForwardUser[]>([]);
  const [selected, setSelected] = useState<ApplicationLead | null>(null);
  const [filter, setFilter] = useState<"all" | JobApplyType>("all");
  const [statusFilter, setStatusFilter] = useState<"all" | ApplicationStatus>("all");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [forwarding, setForwarding] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pendingDeleteNote, setPendingDeleteNote] = useState<{
    app: ApplicationLead;
    note: ApplicationNote;
  } | null>(null);
  const [pendingDeleteApp, setPendingDeleteApp] = useState<ApplicationLead | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [data, users] = await Promise.all([
        listApplications(),
        canForwardApplications ? listApplicationForwardUsers() : Promise.resolve([]),
      ]);
      setApps(data);
      setForwardUsers(users);
      setSelected((current) =>
        current ? data.find((app) => app.id === current.id) ?? data[0] ?? null : data[0] ?? null,
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not load applications.");
    } finally {
      setLoading(false);
    }
  }, [canForwardApplications]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const filtered = useMemo(
    () =>
      apps.filter(
        (app) =>
          (filter === "all" || app.type === filter) &&
          (statusFilter === "all" || app.status === statusFilter),
      ),
    [apps, filter, statusFilter],
  );

  const counts = useMemo(
    () => ({
      all: apps.length,
      new: apps.filter((app) => app.status === "new").length,
      inReview: apps.filter((app) => app.status === "in_review").length,
      approved: apps.filter((app) => app.status === "approved").length,
    }),
    [apps],
  );

  async function setStatus(id: string, status: ApplicationStatus) {
    setSaving(true);
    setError(null);
    try {
      await updateApplication(id, { status });
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not update application.");
    } finally {
      setSaving(false);
    }
  }

  async function saveNotes(app: ApplicationLead, note: string) {
    setSaving(true);
    setError(null);
    try {
      await addApplicationNote(app.id, note);
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not save notes.");
    } finally {
      setSaving(false);
    }
  }

  async function deleteNote(app: ApplicationLead, note: ApplicationNote) {
    setPendingDeleteNote({ app, note });
  }

  async function confirmDeleteNote() {
    if (!pendingDeleteNote) return;
    setSaving(true);
    setError(null);
    try {
      await deleteApplicationNote(pendingDeleteNote.app.id, pendingDeleteNote.note.id);
      setPendingDeleteNote(null);
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not delete note.");
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(app: ApplicationLead) {
    setPendingDeleteApp(app);
  }

  async function confirmDeleteApplication() {
    if (!pendingDeleteApp) return;
    setDeleting(true);
    setError(null);
    try {
      await deleteApplication(pendingDeleteApp.id);
      setPendingDeleteApp(null);
      setSelected(null);
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not delete application.");
    } finally {
      setDeleting(false);
    }
  }

  async function handleForward(app: ApplicationLead, assignedTo: string, forwardNote: string) {
    if (!assignedTo) {
      setError("Choose a user to forward this application to.");
      return;
    }
    setForwarding(true);
    setError(null);
    try {
      await forwardApplication(app.id, assignedTo, forwardNote);
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not forward application.");
    } finally {
      setForwarding(false);
    }
  }

  async function openResume(path: string) {
    try {
      const url = await getResumeUrl(path);
      if (!url) {
        window.alert("Resume download is available after Supabase storage is connected.");
        return;
      }
      // Trigger a real download (the signed URL carries an attachment header) via a
      // temporary link, so the app stays put and no blank tab opens for .docx/.pdf.
      const a = document.createElement("a");
      a.href = url;
      a.rel = "noopener";
      a.download = path.split("/").pop() ?? "resume";
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch {
      window.alert("Could not open resume. Check storage permissions.");
    }
  }

  return (
    <>
      <Seo title="Applications" noindex />
      <DemoBanner />

      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">Applications inbox</h1>
          <p className="mt-1 max-w-2xl text-sm text-brand-500">
            Review driver, owner-operator, and office applications. {scopeNote} Status
            and notes persist to Supabase in production, with localStorage fallback
            in preview mode.
          </p>
        </div>
        <div className="grid w-full grid-cols-3 gap-2 text-center text-xs sm:w-auto">
          <div className="rounded-lg border border-brand-100 px-3 py-2">
            <div className="text-lg font-bold text-brand-900">{counts.new}</div>
            New
          </div>
          <div className="rounded-lg border border-brand-100 px-3 py-2">
            <div className="text-lg font-bold text-brand-900">{counts.inReview}</div>
            In review
          </div>
          <div className="rounded-lg border border-brand-100 px-3 py-2">
            <div className="text-lg font-bold text-brand-900">{counts.approved}</div>
            Approved
          </div>
        </div>
      </div>

      {error && (
        <FeedbackMessage tone="error" className="mt-4">
          {error}
        </FeedbackMessage>
      )}

      <div className="mt-6 grid gap-3 sm:grid-cols-2 lg:max-w-2xl">
        <div>
          <Label htmlFor="application-type-filter">Application type</Label>
          <select
            id="application-type-filter"
            value={filter}
            onChange={(e) => setFilter(e.target.value as "all" | JobApplyType)}
            className="mt-1 flex h-10 w-full rounded-md border border-brand-200 bg-white px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-500"
          >
            {filters.map((item) => (
              <option key={item.value} value={item.value}>
                {item.label}
              </option>
            ))}
          </select>
        </div>
        <div>
          <Label htmlFor="application-status-filter">Status</Label>
          <select
            id="application-status-filter"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as "all" | ApplicationStatus)}
            className="mt-1 flex h-10 w-full rounded-md border border-brand-200 bg-white px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-500"
          >
            {statusFilters.map((item) => (
              <option key={item.value} value={item.value}>
                {item.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {loading ? (
        <div className="mt-6 grid gap-3 lg:grid-cols-[minmax(0,1fr)_420px]">
          <div className="grid gap-3">
            <CardSkeleton />
            <CardSkeleton />
            <CardSkeleton />
          </div>
          <CardSkeleton rows={6} />
        </div>
      ) : (
        <div className="mt-6 grid min-w-0 items-start gap-6 lg:grid-cols-[minmax(0,1fr)_420px]">
          <div className="grid content-start gap-3">
            {filtered.length === 0 && (
              <EmptyState
                title="No applications match"
                body="Try changing the type or status filter. Rejected applications stay saved unless an admin/manager deletes them."
              />
            )}
            {filtered.map((app) => {
              const Icon = typeIcon[app.type];
              const active = selected?.id === app.id;
              return (
                <button
                  key={app.id}
                  type="button"
                  onClick={() => setSelected(app)}
                  className={`rounded-xl border bg-white p-4 text-left shadow-sm transition ${
                    active
                      ? "border-accent-500 ring-2 ring-accent-500/10"
                      : "border-brand-100 hover:border-brand-200 hover:shadow-md"
                  }`}
                >
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-brand-900 text-white">
                          <Icon className="h-4 w-4" />
                        </span>
                        <div>
                          <p className="font-medium text-brand-900">{app.name}</p>
                          <p className="text-xs text-brand-400">
                            Submitted {fmt(app.createdAt)}
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                      <Badge variant={statusVariant[app.status]}>
                        {statusLabels[app.status]}
                      </Badge>
                      {app.department && (
                        <Badge variant="default">{DEPARTMENT_LABEL[app.department]}</Badge>
                      )}
                      {app.payload.assignedToName && (
                        <Badge variant="accent">Forwarded</Badge>
                      )}
                      <span className="text-xs text-brand-400">
                        {APPLY_TYPE_LABEL[app.type]}
                      </span>
                    </div>
                  </div>
                  <div className="mt-3 flex flex-wrap gap-x-5 gap-y-1 text-xs text-brand-500">
                    <span className="break-all">{app.email}</span>
                    <span>{app.phone}</span>
                    {app.payload.position && <span className="break-words">Position: {app.payload.position}</span>}
                    {app.payload.interest && <span className="break-words">Interest: {app.payload.interest}</span>}
                    {app.payload.assignedToName && (
                      <span className="break-words">Forwarded to: {app.payload.assignedToName}</span>
                    )}
                  </div>
                </button>
              );
            })}
          </div>

          <aside className="min-w-0 rounded-xl border border-brand-100 bg-white p-5 shadow-sm lg:sticky lg:top-6 lg:self-start">
            {selected ? (
              <ApplicationDetail
                application={selected}
                saving={saving}
                deleting={deleting}
                forwarding={forwarding}
                canForward={canForwardApplications}
                forwardUsers={forwardUsers}
                onStatus={setStatus}
                onSaveNotes={saveNotes}
                onDeleteNote={deleteNote}
                onForward={handleForward}
                onOpenResume={openResume}
                onDelete={handleDelete}
              />
            ) : (
              <p className="text-sm text-brand-500">Select an application to review details.</p>
            )}
          </aside>
        </div>
      )}
      <ConfirmDialog
        open={!!pendingDeleteNote}
        title="Delete your note?"
        body="This removes only the note you wrote. The application record stays saved."
        confirmLabel="Delete note"
        tone="danger"
        busy={saving}
        onConfirm={() => void confirmDeleteNote()}
        onCancel={() => setPendingDeleteNote(null)}
      />
      <ConfirmDialog
        open={!!pendingDeleteApp}
        title="Delete application permanently?"
        body={`This removes ${pendingDeleteApp?.name ?? "this applicant"}'s application, including any uploaded resume, from the database. This cannot be undone.`}
        confirmLabel="Delete permanently"
        tone="danger"
        busy={deleting}
        onConfirm={() => void confirmDeleteApplication()}
        onCancel={() => setPendingDeleteApp(null)}
      />
    </>
  );
}

function ApplicationDetail({
  application,
  saving,
  deleting,
  forwarding,
  canForward,
  forwardUsers,
  onStatus,
  onSaveNotes,
  onDeleteNote,
  onForward,
  onOpenResume,
  onDelete,
}: {
  application: ApplicationLead;
  saving: boolean;
  deleting: boolean;
  forwarding: boolean;
  canForward: boolean;
  forwardUsers: ForwardUser[];
  onStatus: (id: string, status: ApplicationStatus) => void;
  onSaveNotes: (app: ApplicationLead, note: string) => Promise<void>;
  onDeleteNote: (app: ApplicationLead, note: ApplicationNote) => Promise<void>;
  onForward: (app: ApplicationLead, assignedTo: string, forwardNote: string) => void;
  onOpenResume: (path: string) => void;
  onDelete: (app: ApplicationLead) => void;
}) {
  const [noteDraft, setNoteDraft] = useState("");
  const [assignedTo, setAssignedTo] = useState(application.payload.assignedTo ?? "");
  const [forwardNote, setForwardNote] = useState("");
  const payload = application.payload;

  useEffect(() => {
    setNoteDraft("");
    setAssignedTo(application.payload.assignedTo ?? "");
    setForwardNote("");
  }, [application.id, application.payload.assignedTo]);

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h2 className="break-words text-lg font-bold text-brand-900">{application.name}</h2>
          <p className="text-sm text-brand-500">
            {APPLY_TYPE_LABEL[application.type]}
            {application.department ? ` · ${DEPARTMENT_LABEL[application.department]}` : ""}
          </p>
        </div>
        <Badge variant={statusVariant[application.status]}>
          {statusLabels[application.status]}
        </Badge>
      </div>

      <div className="space-y-2 text-sm text-brand-700">
        <a href={`mailto:${application.email}`} className="flex min-w-0 items-center gap-2 break-all hover:underline">
          <Mail className="h-4 w-4 text-accent-500" />
          {application.email}
        </a>
        <a href={`tel:${application.phone.replace(/[^+\d]/g, "")}`} className="flex min-w-0 items-center gap-2 break-all hover:underline">
          <Phone className="h-4 w-4 text-accent-500" />
          {application.phone || "No phone"}
        </a>
      </div>

      <div className="mt-5 max-h-72 overflow-y-auto rounded-lg bg-brand-50 p-4 text-sm">
        <div className="grid gap-3">
        <DetailRow label="Position" value={payload.position} />
        <DetailRow label="Interest" value={payload.interest} />
        <DetailRow label="CDL state" value={payload.cdlState} />
        <DetailRow label="Experience" value={payload.experienceYears != null ? `${payload.experienceYears} years` : ""} />
        <DetailRow label="Tanker endorsement" value={payload.endorsementTanker ? "Yes" : ""} />
        <DetailRow label="HazMat endorsement" value={payload.endorsementHazmat ? "Yes" : ""} />
        <DetailRow label="TWIC card" value={payload.endorsementTwic ? "Yes" : ""} />
        <DetailRow label="Offload — air compressor" value={payload.offloadAirCompressor ? "Yes" : ""} />
        <DetailRow label="Offload — pump" value={payload.offloadPump ? "Yes" : ""} />
        <DetailRow label="Authority" value={payload.authority === "own" ? "Own MC/DOT" : payload.authority === "lease" ? "Run under BB authority" : ""} />
        <DetailRow label="DOT" value={payload.dotNumber} />
        <DetailRow label="MC" value={payload.mcNumber} />
        <DetailRow label="Truck" value={payload.truck} />
        <DetailRow
          label="Source"
          value={payload.source ? sourceLabel[payload.source] ?? payload.source : ""}
        />
        <DetailRow label="Referral" value={payload.referralName} />
        <DetailRow
          label="Contact consent"
          value={payload.consentToContact ? "Yes - phone/text/email" : ""}
        />
        <DetailRow label="Applicant notes" value={payload.notes} />
        <DetailRow label="Forwarded to" value={payload.assignedToName} />
        <DetailRow label="Forward note" value={payload.forwardNote} />
        </div>
      </div>

      {payload.resumePath && (
        <Button
          type="button"
          variant="outline"
          className="mt-4 w-full"
          onClick={() => onOpenResume(payload.resumePath!)}
        >
          <Download className="h-4 w-4" />
          Download resume
        </Button>
      )}

      <div className="mt-5">
        <Label htmlFor={`notes-${application.id}`} className="flex items-center gap-1">
          <StickyNote className="h-4 w-4" />
          Add internal note
        </Label>
        <Textarea
          id={`notes-${application.id}`}
          value={noteDraft}
          onChange={(e) => setNoteDraft(e.target.value)}
          placeholder="Call notes, next steps, recruiter assignment..."
          className="mt-1"
        />
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="mt-2 w-full sm:w-auto"
          disabled={saving || noteDraft.trim().length === 0}
          onClick={async () => {
            await onSaveNotes(application, noteDraft);
            setNoteDraft("");
          }}
        >
          Save notes
        </Button>
      </div>

      <div className="mt-5">
        <div className="flex items-center justify-between gap-3">
          <h3 className="text-sm font-semibold text-brand-900">Note history</h3>
          {application.notes.length > 0 && (
            <span className="text-xs text-brand-400">
              {application.notes.length} {application.notes.length === 1 ? "note" : "notes"}
            </span>
          )}
        </div>
        {application.notes.length === 0 ? (
          <p className="mt-2 rounded-lg border border-dashed border-brand-200 p-3 text-sm text-brand-500">
            No internal notes yet.
          </p>
        ) : (
          <div className="mt-2 max-h-56 overflow-y-auto rounded-lg border border-brand-100 bg-brand-50 p-2">
            <div className="grid gap-2">
              {application.notes.map((note) => (
                <article key={note.id} className="rounded-md border border-brand-100 bg-white p-3 shadow-sm">
                  <p className="whitespace-pre-wrap text-sm text-brand-800">{note.body}</p>
                  <div className="mt-2 flex items-center justify-between gap-2">
                    <span className="min-w-0 break-words text-xs text-brand-400">By {note.authorName}</span>
                    {note.canDelete && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="h-7 px-2 text-xs text-red-600 hover:bg-red-50"
                        disabled={saving}
                        onClick={() => onDeleteNote(application, note)}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                        Delete
                      </Button>
                    )}
                  </div>
                </article>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="mt-5 grid gap-2 sm:grid-cols-2">
        <Button
          type="button"
          variant="outline"
          disabled={saving}
          onClick={() => onStatus(application.id, "in_review")}
        >
          Mark in review
        </Button>
        <Button
          type="button"
          variant="accent"
          disabled={saving}
          onClick={() => onStatus(application.id, "approved")}
        >
          Approve
        </Button>
        <Button
          type="button"
          variant="outline"
          className="text-red-600 hover:bg-red-50 sm:col-span-2"
          disabled={saving}
          onClick={() => onStatus(application.id, "rejected")}
        >
          Reject
        </Button>
      </div>

      {canForward && (
        <div className="mt-5 rounded-lg border border-brand-100 p-4">
          <Label htmlFor={`forward-${application.id}`} className="flex items-center gap-1">
            <Forward className="h-4 w-4" />
            Forward to user
          </Label>
          <select
            id={`forward-${application.id}`}
            value={assignedTo}
            onChange={(e) => setAssignedTo(e.target.value)}
            className="mt-2 flex h-10 w-full rounded-md border border-brand-200 bg-white px-3 text-sm"
            disabled={forwarding}
          >
            <option value="">Select internal user...</option>
            {forwardUsers.map((target) => (
              <option key={target.id} value={target.id}>
                {target.fullName || target.email}
                {target.department ? ` - ${DEPARTMENT_LABEL[target.department]}` : " - Admin"}
                {target.staffLevel ? ` ${target.staffLevel}` : ""}
              </option>
            ))}
          </select>
          <Textarea
            value={forwardNote}
            onChange={(e) => setForwardNote(e.target.value)}
            placeholder="Optional note for the person you are forwarding this to..."
            className="mt-2"
            disabled={forwarding}
          />
          <Button
            type="button"
            variant="outline"
            className="mt-2 w-full"
            disabled={forwarding}
            onClick={() => onForward(application, assignedTo, forwardNote)}
          >
            <Forward className="h-4 w-4" />
            {forwarding ? "Forwarding..." : "Forward application"}
          </Button>
        </div>
      )}

      <div className="mt-5 border-t border-brand-100 pt-4">
        <p className="text-xs text-brand-400">
          Rejecting keeps the record (filter by "Rejected" to find it later). Deleting
          removes it and any resume from the database permanently.
        </p>
        <Button
          type="button"
          variant="outline"
          className="mt-2 w-full text-red-600 hover:bg-red-50"
          disabled={deleting}
          onClick={() => onDelete(application)}
        >
          <Trash2 className="h-4 w-4" />
          {deleting ? "Deleting..." : "Delete permanently"}
        </Button>
      </div>
    </div>
  );
}

function DetailRow({ label, value }: { label: string; value?: string | number | null }) {
  if (value === undefined || value === null || value === "") return null;
  return (
    <div>
      <div className="text-xs font-semibold uppercase tracking-wide text-brand-400">
        {label}
      </div>
      <div className="break-words text-brand-800">{value}</div>
    </div>
  );
}

function EmptyState({ title, body }: { title: string; body: string }) {
  return (
    <div className="rounded-xl border border-dashed border-brand-200 bg-white p-6 text-center">
      <p className="font-medium text-brand-900">{title}</p>
      <p className="mt-1 text-sm text-brand-500">{body}</p>
    </div>
  );
}
